import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { readThinkerConfig, readTransformReply } from "~/api/commentTypes";
import type { MessageDraft } from "~/api/conversationTypes";
import { useApi } from "~/api/provider";
import {
  useAppendConversationMessage,
  useAvailableThinkers,
  useConversationDetail,
  useStartConversation,
} from "~/api/queries";
import type { Document } from "~/api/types";
import { DiffView } from "~/features/comments/DiffView";
import { diffLines } from "~/features/comments/diff";
import { rangeWriteBlockedReason } from "~/features/conversations/documentWrite";
import type { DocumentPorts } from "~/features/conversations/ports/editorPorts";
import { ThinkerPicker } from "~/features/thinkers/ThinkerPicker";
import { Button } from "~/foundation/components/Button";
import { Spinner } from "~/foundation/components/Spinner";
import { Switch } from "~/foundation/components/Switch";
import { Text } from "~/foundation/components/Text";

/**
 * A comment thread that is also a conversation (W8 §B.9, §E.2 — the owner's delta).
 *
 * **The identity fusion is the whole design, and it is one line of contract:** the comment
 * thread's id IS the conversation's id. kms mints a single `uuid` and uses it as the anchor
 * mark's id AND as the `id` it POSTs to the turn face, which accepts a client-supplied id.
 * There is no join table, no join column, and nothing to keep in step — a thread "is a think
 * thread" exactly when a conversation with its id exists.
 *
 * Everything below follows from that:
 *
 *  - the thinker selection is persisted as a **`config` message**, because the server has no
 *    other table for it, and restored by scanning messages backwards for the last one
 *    (`readThinkerConfig` — no component scans a message array itself);
 *  - the reply box is an **instruction box**: what you type is not a comment, it is the
 *    instruction sent to every selected thinker, one call each;
 *  - each result is written back as a **`transform` message** keyed to the comment by
 *    `metadata.parentCommentId` — so the AI side needs no reply tree at all, just a flat list
 *    with a parent id on it.
 *
 * The one place slate deliberately differs from kms: the apply goes through the SAME write
 * path as every other apply in this wave. kms's was the single path that used a raw
 * `tr.insertText`, which is why it could only ever insert plain text.
 */
export function ThinkThread({
  threadId,
  document,
  anchorText,
  ports,
  canComment,
}: {
  threadId: string;
  document: Document;
  /** The text this thread was made from. It is what a transform rewrites, and how we find it. */
  anchorText: string;
  ports: DocumentPorts;
  canComment: boolean;
}) {
  const { t } = useTranslation();
  const api = useApi();
  const detail = useConversationDetail(threadId);
  const startConversation = useStartConversation();
  const append = useAppendConversationMessage();
  const { data: thinkers } = useAvailableThinkers();

  const [pickerOpen, setPickerOpen] = useState(false);
  const [instruction, setInstruction] = useState("");
  const [running, setRunning] = useState(false);
  const [autoApply, setAutoApply] = useState(false);
  const [applied, setApplied] = useState<string[]>([]);
  const [dismissed, setDismissed] = useState<string[]>([]);
  const [notice, setNotice] = useState<string | null>(null);

  const conversation = detail.data ?? null;
  const messages = useMemo(() => conversation?.messages ?? [], [conversation]);

  const stored = useMemo(
    () =>
      readThinkerConfig(
        messages.map((m) => ({ messageType: m.messageType, metadata: m.metadata.raw })),
      ),
    [messages],
  );
  const [selected, setSelected] = useState<string[]>([]);
  // The stored selection wins until somebody changes it in this session.
  useEffect(() => {
    if (stored && stored.thinkerIds.length > 0) {
      setSelected(stored.thinkerIds);
    }
  }, [stored]);

  const replies = useMemo(
    () =>
      messages
        .filter((message) => message.messageType === "transform")
        .map((message) => readTransformReply(message.id, message.createdAt, message.metadata.raw))
        .filter((reply): reply is NonNullable<typeof reply> => reply !== null)
        .filter((reply) => reply.parentCommentId === threadId),
    [messages, threadId],
  );

  const nameOf = (id: string) => thinkers?.find((entry) => entry.id === id)?.name ?? id;
  // A transform replaces exactly the text the thread is about, so it needs the RANGE gate:
  // there is no whole-body fallback that could honour "these words and no others".
  const blocked = rangeWriteBlockedReason(document, ports, t);

  const persistSelection = (ids: string[]) => {
    setSelected(ids);
    const draft: MessageDraft = {
      content: `Thinkers selected: ${ids.length}`,
      author: "System",
      authorType: "system",
      messageType: "config",
      metadata: { thinkerIds: ids, thinkerNames: ids.map(nameOf) },
    };
    append.mutate({ id: threadId, message: draft });
  };

  /**
   * Fan out: ONE call per selected thinker, each written back as its own row.
   *
   * Failures are per-thinker and reported per-thinker. A thinker that is offline must not
   * cost you the two answers that did arrive — which is what a single Promise.all rejection
   * would have done.
   */
  const runTransform = async () => {
    const instructions = instruction.trim();
    if (!instructions || selected.length === 0) {
      return;
    }
    setRunning(true);
    setNotice(null);
    setInstruction("");

    // The thread must exist as a conversation before a result can be written to it. This is
    // the identity fusion in code: the comment thread's id, POSTed as the conversation's id.
    if (!conversation) {
      try {
        await startConversation.mutateAsync({
          id: threadId,
          documentId: document.id,
          title: instructions.slice(0, 80),
          anchors: [{ selectedText: anchorText, anchorType: "selection" }],
        });
      } catch (error) {
        setRunning(false);
        setNotice(error instanceof Error ? error.message : t("Couldn't start the think thread"));
        return;
      }
    }

    for (const thinkerId of selected) {
      try {
        const outcome = await api.selection.transform({
          documentId: document.id,
          ...(document.collectionId ? { collectionId: document.collectionId } : {}),
          documentTitle: document.title,
          instructions,
          selectedText: anchorText,
          thinkerId,
        });
        if (outcome.kind === "dispatch") {
          // 202: the resolved thinker is pinned to somebody's editor window. Saying so beats a
          // spinner that never resolves — the result lands on a later poll.
          setNotice(
            t("{{name}} is answering in a connected editor — the reply will appear here.", {
              name: outcome.thinkerName ?? nameOf(thinkerId),
            }),
          );
          continue;
        }
        await append.mutateAsync({
          id: threadId,
          message: {
            content: outcome.text,
            author: outcome.thinkerName ?? nameOf(thinkerId),
            authorType: "ai",
            messageType: "transform",
            metadata: {
              parentCommentId: threadId,
              thinkerId: outcome.thinkerId ?? thinkerId,
              thinkerName: outcome.thinkerName ?? nameOf(thinkerId),
              model: outcome.model,
              instructions,
              selectedText: anchorText,
              updatedText: outcome.text,
            },
          },
        });
        if (autoApply) {
          await applyReply(outcome.text);
        }
      } catch (error) {
        setNotice(
          t("{{name}}: {{reason}}", {
            name: nameOf(thinkerId),
            reason: error instanceof Error ? error.message : t("no answer"),
          }),
        );
      }
    }
    setRunning(false);
  };

  /**
   * Write a result back over the text this thread is about.
   *
   * The range is found by TEXT, not by a stored offset: offsets go stale the moment anybody
   * types above the selection, and writing to a stale offset silently overwrites the wrong
   * words. If the text is no longer there, this refuses in the words kms used.
   */
  const applyReply = async (updatedText: string): Promise<boolean> => {
    if (blocked) {
      setNotice(blocked);
      return false;
    }
    const range = ports.findText?.(anchorText) ?? null;
    if (!range || !ports.actions?.canWrite) {
      setNotice(
        range
          ? (ports.unavailableReason ?? t("Editor not available"))
          : t("Selection changed. Please reselect the text."),
      );
      return false;
    }
    const wrote = await ports.actions.applyAtRange(updatedText, range, {
      author: { kind: "ai", agent: t("Comment transform") },
    });
    setNotice(wrote ? t("Applied changes") : t("Couldn't write to the document"));
    return wrote;
  };

  if (detail.isLoading) {
    return <Spinner label={t("Loading")} />;
  }

  return (
    <div className="flex flex-col gap-2 rounded-md border border-divider border-dashed p-2">
      <header className="flex flex-wrap items-center gap-2">
        <Text variant="small" className="font-medium text-text">
          {t("Thinkers")}
        </Text>
        <Text variant="small" className="min-w-0 flex-1 truncate text-text-tertiary">
          {selected.length === 0 ? t("None selected") : selected.map((id) => nameOf(id)).join(", ")}
        </Text>
        <Button
          size="sm"
          variant="ghost"
          disabled={!canComment}
          onClick={() => setPickerOpen(true)}
        >
          {selected.length === 0 ? t("Select") : t("Change")}
        </Button>
      </header>

      {(thinkers ?? []).length === 0 ? (
        <Text variant="small" className="text-text-tertiary">
          {t("No thinkers configured for this project yet.")}
        </Text>
      ) : null}

      {replies.map((reply) => {
        const isApplied = applied.includes(reply.messageId);
        const isDismissed = dismissed.includes(reply.messageId);
        return (
          <article key={reply.messageId} className="flex flex-col gap-1">
            <header className="flex items-center gap-2">
              <Text variant="small" className="font-medium text-text">
                {reply.thinkerName ?? t("Assistant")}
              </Text>
              {reply.model ? (
                <span className="text-caption text-text-tertiary">{reply.model}</span>
              ) : null}
              {isApplied ? (
                <span className="ms-auto text-caption text-success">{t("Applied")}</span>
              ) : isDismissed ? (
                <span className="ms-auto text-caption text-text-tertiary">{t("Dismissed")}</span>
              ) : null}
            </header>
            <DiffView
              lines={diffLines({
                diff: reply.diff,
                before: reply.selectedText ?? anchorText,
                after: reply.updatedText,
              })}
            />
            {!isApplied && !isDismissed ? (
              <div className="flex flex-wrap items-center gap-1">
                <Button
                  size="sm"
                  variant="primary"
                  disabled={!canComment || Boolean(blocked) || !reply.updatedText}
                  onClick={async () => {
                    if (!reply.updatedText) {
                      setNotice(t("No updated text available to apply"));
                      return;
                    }
                    if (await applyReply(reply.updatedText)) {
                      setApplied((current) => [...current, reply.messageId]);
                    }
                  }}
                >
                  {t("Apply changes")}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setDismissed((current) => [...current, reply.messageId])}
                >
                  {t("Dismiss")}
                </Button>
                {blocked ? (
                  <Text variant="small" className="text-text-tertiary">
                    {blocked}
                  </Text>
                ) : null}
              </div>
            ) : null}
          </article>
        );
      })}

      {running ? (
        <Text variant="small" className="text-text-tertiary">
          {t("Thinking…")}
        </Text>
      ) : null}

      <Switch
        checked={autoApply}
        disabled={!canComment || Boolean(blocked)}
        label={t("Apply automatically")}
        description={t("Write each answer straight into the document, without reviewing it.")}
        onCheckedChange={setAutoApply}
      />

      <div className="flex flex-col gap-1">
        <textarea
          value={instruction}
          aria-label={t("Instruction")}
          placeholder={t("Tell the thinkers what to change about this text…")}
          disabled={!canComment}
          onChange={(event) => setInstruction(event.target.value)}
          className="min-h-16 w-full rounded-sm border border-input-border bg-bg p-2 text-body text-text disabled:opacity-50"
        />
        <Button
          size="sm"
          variant="primary"
          className="self-start"
          disabled={
            !canComment || running || instruction.trim().length === 0 || selected.length === 0
          }
          onClick={() => void runTransform()}
        >
          {running ? t("Thinking…") : t("Ask the thinkers")}
        </Button>
        {selected.length === 0 ? (
          <Text variant="small" className="text-text-tertiary">
            {t("Select at least one thinker first.")}
          </Text>
        ) : null}
      </div>

      <div aria-live="polite" className="min-h-4 text-caption text-text-tertiary">
        {notice}
      </div>

      <ThinkerPicker
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        selected={selected}
        onSelect={persistSelection}
        multiple
        filter="ai"
        documentId={document.id}
        title={t("Who should answer?")}
        confirmLabel={t("Use selected thinkers")}
      />
    </div>
  );
}
